<template>
  <div class="production-plan-orders pb-6">
    <!-- READ-ONLY ORDERS SECTION -->
    <template v-if="readonlyOrders.length > 0">
      <div class="is-flex is-align-items-center gap-4 mb-5">
        <div class="po-divider-line" />
        <span
          class="is-size-7 has-text-weight-bold has-text-grey-light is-uppercase"
          style="letter-spacing: 0.1em; white-space: nowrap"
        >
          Active &amp; Completed Orders
        </span>
        <div class="po-divider-line" />
      </div>

      <div class="orders-section mb-5">
        <OrderGroup
          v-for="order in readonlyOrders"
          :key="order.id"
          class="mb-4"
          :supplier-label="
            supplierLabel(order.supplier_id != null ? String(order.supplier_id) : '__none__')
          "
          :items="order.items.map((i) => ({ ...i, key: i.order_item_id }))"
          :persisted-order="{
            visual_id: order.visual_id,
            path: order.path,
            state: order.state ?? null,
          }"
          :readonly="true"
        />
      </div>
    </template>

    <!-- DRAFT ORDERS SECTION -->
    <template v-if="orderItems.length > 0 || persistedOrders.size > 0">
      <div class="is-flex is-align-items-center gap-4 mb-5">
        <div class="po-divider-line" />
        <span
          class="is-size-7 has-text-weight-bold has-text-grey-light is-uppercase"
          style="letter-spacing: 0.1em; white-space: nowrap"
        >
          Draft Purchase Orders
        </span>
        <div class="po-divider-line" />
      </div>

      <div class="orders-section mb-5">
        <div class="is-flex is-align-items-center is-justify-content-space-between mb-4">
          <div class="is-flex is-align-items-center gap-3">
            <h2 class="is-family-secondary has-text-weight-black is-size-5">Orders</h2>
            <span class="tag">
              {{ orderGroups.length }} {{ orderGroups.length === 1 ? "order" : "orders" }}
            </span>
            <span v-if="doneCount > 0" class="tag is-info is-light">{{ doneCount }} created</span>
            <span class="is-size-7 has-text-grey">· {{ orderItems.length }} total items</span>
          </div>
          <button
            class="button"
            data-create-all
            :class="[allCreated ? '' : 'is-primary', { 'is-loading': anyCreating }]"
            :disabled="allCreated || anyGroupInvalid || anyCreating"
            :title="
              anyGroupInvalid && !allCreated
                ? 'All order items need a positive quantity'
                : undefined
            "
            @click="showCreateAllConfirm = true"
          >
            {{ createAllLabel }}
          </button>
        </div>

        <OrderGroup
          v-for="[supplierKey, items] in orderGroups"
          :key="supplierKey"
          class="mb-4"
          :supplier-label="supplierLabel(supplierKey)"
          :items="items"
          :persisted-order="persistedOrders.get(supplierKey) || null"
          :is-creating="creating.has(supplierKey)"
          :is-dirty="isDirtyGroup(supplierKey)"
          :is-updating="updating.has(supplierKey)"
          @update-item="({ key, patch }) => updateOrderItem(key, patch)"
          @remove-item="({ key, orderItemId }) => removeItem(supplierKey, key, orderItemId)"
          @create="createOrder(supplierKey)"
          @update="updateOrder(supplierKey)"
        />
      </div>
    </template>

    <!-- NO ORDERS YET -->
    <div
      v-if="orderItems.length === 0 && persistedOrders.size === 0 && readonlyOrders.length === 0"
      class="has-text-centered py-5 mb-5 po-orders-empty"
      data-no-orders
    >
      <p class="has-text-grey-light has-text-weight-bold mb-1">No orders for this plan yet</p>
      <p class="is-size-7 has-text-grey-light">Add ingredients below to start building orders.</p>
    </div>

    <!-- INGREDIENTS SECTION -->
    <div class="box p-0 mb-5 po-ingredients-box">
      <div
        class="px-5 py-4 is-flex is-align-items-center is-flex-wrap-wrap gap-3 po-section-header"
      >
        <h2 class="is-family-secondary has-text-weight-black is-size-5">Ingredients from plan</h2>
        <span class="tag">{{ allIngredients.length }} total</span>
        <span class="tag is-success is-light">{{ needsOrdering.length }} to purchase</span>
        <span class="tag">{{ alreadyCovered.length }} in stock</span>
        <div class="ml-auto">
          <div class="buttons has-addons">
            <button
              v-for="[val, label] in filterOptions"
              :key="val"
              class="button is-small"
              :class="filter === val ? 'is-primary is-selected' : ''"
              @click="filter = val"
            >
              {{ label }}
            </button>
          </div>
        </div>
      </div>

      <!-- Bulk bar -->
      <div v-if="selectedIds.size > 0" class="px-5 py-3 po-bulk-bar">
        <div class="is-flex is-align-items-center is-flex-wrap-wrap gap-3">
          <span class="tag is-primary is-medium">{{ selectedIds.size }} selected</span>
          <span class="is-size-7 has-text-weight-bold">Set supplier:</span>
          <div class="select is-small">
            <select v-model="bulkSupplierId">
              <option value="">— No supplier —</option>
              <option v-for="s in allSuppliers" :key="s.id" :value="String(s.id)">
                {{ s.name }}
              </option>
            </select>
          </div>
          <button
            class="button is-primary is-small"
            :disabled="!canAddBulk"
            :title="!canAddBulk ? 'All selected items need a positive quantity' : undefined"
            @click="addBulk"
          >
            + Add {{ selectedIds.size }} to orders
          </button>
          <button class="button is-ghost is-small ml-auto" @click="clearSelection">✕ Clear</button>
        </div>
      </div>

      <!-- Table -->
      <div style="overflow-x: auto">
        <table class="table is-fullwidth is-hoverable po-ingredients-table mb-0">
          <thead>
            <tr>
              <th style="width: 40px; text-align: center">
                <input
                  type="checkbox"
                  :checked="allChecked"
                  :indeterminate.prop="someChecked && !allChecked"
                  @change="toggleAll($event.target.checked)"
                />
              </th>
              <th>Ingredient</th>
              <th>Need</th>
              <th>On-hand</th>
              <th>
                <span class="is-flex is-align-items-center gap-1">
                  On-order
                  <IconHint
                    content="Quantities on open purchase orders. Only stock arriving before the plan's start date counts toward coverage — later arrivals are shown for reference."
                  />
                </span>
              </th>
              <th>
                <span class="is-flex is-align-items-center gap-1">
                  Still needed
                  <IconHint
                    content="How much still needs to be ordered: required minus on-hand stock and confirmed quantities arriving before the plan's start date."
                  />
                </span>
              </th>
              <th>Purchase qty</th>
              <th>Supplier</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="visibleIngredients.length === 0">
              <td colspan="9" class="has-text-centered has-text-grey-light py-6">
                No ingredients match the current filter.
              </td>
            </tr>
            <IngredientRow
              v-for="ing in visibleIngredients"
              :key="ing.inventory_id"
              :ingredient="ing"
              :draft="currentDraft(ing)"
              :all-suppliers="allSuppliers"
              :needs-purchase="needsPurchaseIds.has(ing.inventory_id)"
              :is-selected="selectedIds.has(ing.inventory_id)"
              :linked-draft="linkedDraftByInventoryId.get(ing.inventory_id) || null"
              :staged-qty-base="stagedQtyByInventoryId.get(ing.inventory_id) || 0"
              :pending-qty="pendingQtyFor(ing)"
              @update-draft="(patch) => updateDraft(ing.inventory_id, patch)"
              @update-pending-qty="(qty) => setPendingQty(ing.inventory_id, qty)"
              @add="(e) => addIngredient(ing, e)"
              @toggle-select="(checked) => toggleOne(ing.inventory_id, checked)"
            />
          </tbody>
        </table>
      </div>

      <!-- Footer -->
      <div
        v-if="addedInventoryIds.size > 0"
        class="px-5 py-3 is-flex is-align-items-center gap-2 po-moved-footer"
      >
        <span class="tag is-success">{{ addedInventoryIds.size }}</span>
        <span class="is-size-7 has-text-grey">
          ingredient{{ addedInventoryIds.size !== 1 ? "s" : "" }} on orders above.
        </span>
        <span v-if="remainingPurchaseCount > 0" class="is-size-7 has-text-grey">
          {{ remainingPurchaseCount }} still pending.
        </span>
      </div>
    </div>

    <!-- Create all dialog -->
    <ConfirmDialog
      v-model="showCreateAllConfirm"
      title="Create all orders"
      confirm-text="Create all"
      confirm-class="is-primary"
      @confirm="createAll"
    >
      Create {{ uncreatedGroups.length }} purchase order{{
        uncreatedGroups.length !== 1 ? "s" : ""
      }}?
      <template v-if="createAllBelowNeededCount > 0">
        <br />
        <span class="is-size-7 has-text-warning-dark">
          Note: {{ createAllBelowNeededCount }} item{{
            createAllBelowNeededCount !== 1 ? "s have" : " has"
          }}
          quantities below the suggested purchase amount.
        </span>
      </template>
    </ConfirmDialog>
  </div>
</template>

<script setup>import { computed as _computed, ref as _ref } from 'vue';

  import IngredientRow from "./production-plan-orders/ingredient-row.vue";
  import OrderGroup from "./production-plan-orders/order-group.vue";
  import ConfirmDialog from "./shared/confirm-dialog.vue";
  import IconHint from "./icon-hint.vue";
  import { toUnit, fmtQty } from "./production-plan-orders/unit-utils.js";
  import { requestJson } from "../controllers/shared/requests.js";
  import { Flash } from "~/jose_modern/flash.js";
  import {
    createOrderItem,
    updateOrderItem as apiUpdateOrderItem,
    deleteOrderItem,
  } from "./api/order-items.js";
  import { useWarnUnsavedChanges } from "./use-warn-unsaved-changes.js";

  const props = defineProps({
    needsOrdering: { type: Array, default: () => [] },
    alreadyCovered: { type: Array, default: () => [] },
    existingDraftOrders: { type: Array, default: () => [] },
    readonlyOrders: { type: Array, default: () => [] },
    allSuppliers: { type: Array, default: () => [] },
    createOrdersPath: { type: String, default: "" },
  });

  const filterOptions = [
    ["purchase", "Purchase needed"],
    ["all", "All ingredients"],
  ];

  let nextKey = 1;

  // --- derived sets ---
  const allIngredients = _computed(() => [...props.needsOrdering, ...props.alreadyCovered]);
  const needsPurchaseIds = _computed(() => new Set(props.needsOrdering.map((i) => i.inventory_id)));

  // --- drafts: inventoryId → { qty, unit, supplier_id } ---
  function buildInitialDrafts() {
    const existingDraftBase = new Map();
    initialItems.forEach((item) => {
      if (!item.order_item_id) return;

      const qty = parseFloat(item.qty);
      if (isNaN(qty) || qty <= 0) return;

      const base = toUnit(qty, item.unit, item.base_unit, item.unit_options);
      existingDraftBase.set(
        item.inventory_id,
        (existingDraftBase.get(item.inventory_id) || 0) + base
      );
    });

    const s = {};
    allIngredients.value.forEach((ing) => {
      s[ing.inventory_id] = {
        unit: ing.unit,
        supplier_id: ing.suppliers?.[0]?.id ?? null,
      };
    });
    return s;
  }

  // --- hydrate from existing linked draft orders ---
  function buildFromExisting() {
    const items = [];
    const orders = new Map();
    props.existingDraftOrders.forEach((order) => {
      const supplierKey = order.supplier_id != null ? String(order.supplier_id) : "__none__";
      orders.set(supplierKey, {
        id: order.id,
        visual_id: order.visual_id,
        path: order.path,
        state: order.state ?? null,
      });
      order.items.forEach((item) => {
        items.push({
          key: nextKey++,
          inventory_id: item.inventory_id,
          name: item.name,
          unit_options: item.unit_options,
          qty: item.qty,
          unit: item.unit,
          supplier_id: order.supplier_id,
          to_purchase: item.to_purchase,
          base_unit: item.base_unit,
          order_item_id: item.order_item_id,
        });
      });
    });
    return { items, orders };
  }

  const { items: initialItems, orders: initialPersisted } = buildFromExisting();

  function buildPersistedItemValues() {
    const map = new Map();
    initialItems.forEach((item) => {
      if (item.order_item_id) map.set(item.order_item_id, { qty: item.qty, unit: item.unit });
    });
    return map;
  }

  // --- state ---
  let drafts = _ref(buildInitialDrafts());
  let selectedIds = _ref(new Set());
  let bulkSupplierId = _ref("");
  let orderItems = _ref(initialItems);
  let persistedOrders = _ref(initialPersisted); // supplierKey → { id, visual_id, path }
  let filter = _ref(props.needsOrdering.length > 0 ? "purchase" : "all");
  let creating = _ref(new Set());
  let updating = _ref(new Set());
  let showCreateAllConfirm = _ref(false);
  let pendingQtyByInventoryId = _ref({});

  const persistedItemValues = buildPersistedItemValues();
  let dirtyOrderItemIds = _ref(new Set());

  // --- derived ---
  const addedInventoryIds = _computed(() => new Set(orderItems.value.map((o) => o.inventory_id)));

  const linkedDraftByInventoryId = _computed(() => {
    const map = new Map();
    orderItems.value.forEach((item) => {
      if (!item.order_item_id) return;
      const supplierKey = item.supplier_id != null ? String(item.supplier_id) : "__none__";
      const order = persistedOrders.value.get(supplierKey);
      if (order) map.set(item.inventory_id, { item, order });
    });
    return map;
  });

  const stagedQtyByInventoryId = _computed(() => {
    const map = new Map();
    orderItems.value.forEach((item) => {
      if (item.order_item_id) return;
      const qty = parseFloat(item.qty);
      if (isNaN(qty) || qty <= 0) return;
      const base = toUnit(qty, item.unit, item.base_unit, item.unit_options);
      map.set(item.inventory_id, (map.get(item.inventory_id) || 0) + base);
    });
    return map;
  });

  const visibleIngredients = _computed(() =>
    filter.value === "purchase" ? props.needsOrdering : allIngredients.value
  );

  const visiblePurchaseIds = _computed(() => visibleIngredients.value.map((i) => i.inventory_id));

  const allChecked = _computed(
    () => visiblePurchaseIds.value.length > 0 && visiblePurchaseIds.value.every((id) => selectedIds.value.has(id))
  );

  const someChecked = _computed(() => visiblePurchaseIds.value.some((id) => selectedIds.value.has(id)));

  const canAddBulk = _computed(() =>
    [...selectedIds.value].every((id) => {
      const ing = allIngredients.value.find((i) => i.inventory_id === id);
      return ing && parseFloat(pendingQtyFor(ing)) > 0;
    })
  );

  const remainingPurchaseCount = _computed(
    () => props.needsOrdering.filter((i) => !addedInventoryIds.value.has(i.inventory_id)).length
  );

  const orderGroups = _computed(() => {
    const map = new Map();
    orderItems.value.forEach((item) => {
      const key = item.supplier_id != null ? String(item.supplier_id) : "__none__";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    });
    persistedOrders.value.forEach((_, key) => {
      if (!map.has(key)) map.set(key, []);
    });
    return [...map.entries()];
  });

  const doneCount = _computed(() => orderGroups.value.filter(([k]) => persistedOrders.value.has(k)).length);
  const allCreated = _computed(() => orderGroups.value.length > 0 && doneCount.value === orderGroups.value.length);
  const anyGroupInvalid = _computed(() =>
    orderGroups.value.some(([, items]) => items.some((i) => !(parseFloat(i.qty) > 0)))
  );
  const anyCreating = _computed(() => creating.value.size > 0);

  const uncreatedGroups = _computed(() => orderGroups.value.filter(([k]) => !persistedOrders.value.has(k)));

  const createAllBelowNeededCount = _computed(() => {
    let count = 0;
    uncreatedGroups.value.forEach(([, items]) => {
      items.forEach((item) => {
        const suggested = toUnit(
          Math.max(item.to_purchase ?? 0, 0),
          item.base_unit,
          item.unit,
          item.unit_options
        );
        const entered = parseFloat(item.qty);
        if (!isNaN(entered) && suggested > 0 && entered < suggested - 0.001) count++;
      });
    });
    return count;
  });

  const remaining = _computed(() => orderGroups.value.length - doneCount.value);

  const createAllLabel = _computed(() => {
    if (allCreated.value) return "✓ All orders created";
    if (remaining.value === orderGroups.value.length) return `✓ Create all orders`;
    return `✓ Create ${remaining.value} remaining order${remaining.value !== 1 ? "s" : ""}`;
  });

  // --- helpers ---
  function currentDraft(ing) {
    return drafts.value[ing.inventory_id] || { unit: ing.unit, supplier_id: null };
  }

  function pendingQtyFor(ing) {
    const typed = pendingQtyByInventoryId.value[ing.inventory_id];
    if (typed != null) return typed;
    const linked = linkedDraftByInventoryId.value.get(ing.inventory_id);
    const linkedBase = linked
      ? toUnit(
          parseFloat(linked.item.qty) || 0,
          linked.item.unit,
          linked.item.base_unit,
          linked.item.unit_options
        )
      : 0;
    const staged = stagedQtyByInventoryId.value.get(ing.inventory_id) || 0;
    const remaining = Math.max(ing.to_purchase - linkedBase - staged, 0);
    const draft = drafts.value[ing.inventory_id];
    return fmtQty(toUnit(remaining, ing.unit, draft?.unit ?? ing.unit, ing.unit_options));
  }

  function setPendingQty(inventoryId, qty) {
    pendingQtyByInventoryId.value = { ...pendingQtyByInventoryId.value, [inventoryId]: qty };
  }

  function clearPendingQty(inventoryId) {
    if (!(inventoryId in pendingQtyByInventoryId.value)) return;
    const next = { ...pendingQtyByInventoryId.value };
    delete next[inventoryId];
    pendingQtyByInventoryId.value = next;
  }

  function supplierLabel(supplierKey) {
    if (supplierKey === "__none__") return "No supplier assigned";
    const id = parseInt(supplierKey);
    return props.allSuppliers.find((s) => s.id === id)?.name || `Supplier ${supplierKey}`;
  }

  // --- actions ---
  function updateDraft(inventoryId, patch) {
    if (patch.unit && drafts.value[inventoryId]?.unit !== patch.unit) {
      clearPendingQty(inventoryId);
    }
    drafts.value[inventoryId] = { ...drafts.value[inventoryId], ...patch };
  }

  function toggleAll(checked) {
    selectedIds.value = new Set(checked ? visiblePurchaseIds.value : []);
  }

  function toggleOne(inventoryId, checked) {
    const next = new Set(selectedIds.value);
    checked ? next.add(inventoryId) : next.delete(inventoryId);
    selectedIds.value = next;
  }

  function clearSelection() {
    selectedIds.value = new Set();
    bulkSupplierId.value = "";
  }

  function resolveIngredientAdd(ing, supplierId, qty, unit) {
    const linked = linkedDraftByInventoryId.value.get(ing.inventory_id);

    if (linked && String(linked.item.supplier_id) === String(supplierId)) {
      const addBase = toUnit(parseFloat(qty) || 0, unit, ing.unit, ing.unit_options);
      const existingBase = toUnit(
        parseFloat(linked.item.qty) || 0,
        linked.item.unit,
        linked.item.base_unit,
        linked.item.unit_options
      );
      const newQty = fmtQty(
        toUnit(existingBase + addBase, ing.unit, linked.item.unit, linked.item.unit_options)
      );
      return {
        type: "upsert",
        key: linked.item.key,
        qty: newQty,
        unit: linked.item.unit,
        order: linked.order,
      };
    }

    const staged = orderItems.value.find(
      (o) =>
        o.inventory_id === ing.inventory_id &&
        String(o.supplier_id) === String(supplierId) &&
        !o.order_item_id
    );
    if (staged) {
      const addBase = toUnit(parseFloat(qty) || 0, unit, ing.unit, ing.unit_options);
      const existingBase = toUnit(
        parseFloat(staged.qty) || 0,
        staged.unit,
        staged.base_unit,
        staged.unit_options
      );
      const newQty = fmtQty(
        toUnit(existingBase + addBase, ing.unit, staged.unit, staged.unit_options)
      );
      return { type: "upsert", key: staged.key, qty: newQty, unit: staged.unit, order: null };
    }

    return {
      type: "new",
      item: {
        key: nextKey++,
        inventory_id: ing.inventory_id,
        name: ing.name,
        unit_options: ing.unit_options,
        qty,
        unit,
        supplier_id: supplierId,
        to_purchase: ing.to_purchase,
        base_unit: ing.unit,
        ingredient_supplier_ids: (ing.suppliers || []).map((s) => s.id),
      },
    };
  }

  function addIngredient(ing, { qty, unit }) {
    const supplierId = currentDraft(ing).supplier_id;
    const result = resolveIngredientAdd(ing, supplierId, qty, unit);

    if (result.type === "upsert") {
      updateOrderItem(result.key, { qty: result.qty });
      const suffix = result.order ? ` in order ${result.order.visual_id}` : "";
      Flash.notice(`${ing.name} updated to ${result.qty} ${result.unit}${suffix}`);
    } else {
      orderItems.value = [...orderItems.value, result.item];
      Flash.notice(`${ing.name} added to order`);
    }
    clearPendingQty(ing.inventory_id);
  }

  function bulkQtyForIng(ing) {
    return { qty: pendingQtyFor(ing), unit: currentDraft(ing).unit };
  }

  function addBulk() {
    if (selectedIds.value.size === 0) return;
    const supplierId = bulkSupplierId.value ? parseInt(bulkSupplierId.value) : null;
    const newItems = [];

    selectedIds.value.forEach((id) => {
      const ing = allIngredients.value.find((i) => i.inventory_id === id);
      if (!ing) return;
      const { qty, unit } = bulkQtyForIng(ing);
      const result = resolveIngredientAdd(ing, supplierId, qty, unit);

      if (result.type === "upsert") {
        updateOrderItem(result.key, { qty: result.qty });
      } else {
        newItems.push(result.item);
        updateDraft(id, { supplier_id: supplierId });
      }
    });

    if (newItems.length > 0) {
      orderItems.value = [...orderItems.value, ...newItems];
    }

    const next = { ...pendingQtyByInventoryId.value };
    selectedIds.value.forEach((id) => delete next[id]);
    pendingQtyByInventoryId.value = next;

    Flash.notice(
      `${selectedIds.value.size} ingredient${selectedIds.value.size !== 1 ? "s" : ""} added to orders`
    );
    selectedIds.value = new Set();
    bulkSupplierId.value = "";
  }

  function updateOrderItem(key, patch) {
    orderItems.value = orderItems.value.map((o) => (o.key === key ? { ...o, ...patch } : o));
    const item = orderItems.value.find((o) => o.key === key);
    if (!item?.order_item_id) return;

    const persisted = persistedItemValues.get(item.order_item_id);
    if (!persisted) return;

    const next = new Set(dirtyOrderItemIds.value);
    if (item.qty !== persisted.qty || item.unit !== persisted.unit) {
      next.add(item.order_item_id);
    } else {
      next.delete(item.order_item_id);
    }
    dirtyOrderItemIds.value = next;
  }

  async function removeItem(supplierKey, key, orderItemId) {
    if (orderItemId) {
      const order = persistedOrders.value.get(supplierKey);
      if (!order) return;

      try {
        const { response } = await deleteOrderItem(order.id, orderItemId);
        if (!response.ok) throw new Error();
      } catch {
        Flash.error("Failed to remove item. Please try again.");
        return;
      }
    }

    const removed = orderItems.value.find((o) => o.key === key);
    orderItems.value = orderItems.value.filter((o) => o.key !== key);
    if (removed) clearPendingQty(removed.inventory_id);
    Flash.notice("Item removed from order");
  }

  function isDirtyGroup(supplierKey) {
    const items = orderGroups.value.find(([k]) => k === supplierKey)?.[1] ?? [];
    return items.some(
      (i) => (i.order_item_id && dirtyOrderItemIds.value.has(i.order_item_id)) || !i.order_item_id
    );
  }

  useWarnUnsavedChanges(() => orderGroups.value.some(([k]) => isDirtyGroup(k)));

  async function updateOrder(supplierKey) {
    const items = orderGroups.value.find(([k]) => k === supplierKey)?.[1] ?? [];
    const order = persistedOrders.value.get(supplierKey);
    if (!order) return;

    const dirtyItems = items.filter(
      (i) => i.order_item_id && dirtyOrderItemIds.value.has(i.order_item_id)
    );
    const newItems = items.filter((i) => !i.order_item_id);
    if (dirtyItems.length === 0 && newItems.length === 0) return;

    updating.value = new Set([...updating.value, supplierKey]);
    try {
      const results = await Promise.all([
        ...dirtyItems.map((item) =>
          apiUpdateOrderItem(order.id, item.order_item_id, {
            item_id: item.inventory_id,
            quantity: item.qty,
            unit: item.unit,
          }).then(({ response }) => {
            if (!response.ok) throw new Error("server error");
            return { type: "patch", item };
          })
        ),
        ...newItems.map((item) =>
          createOrderItem(order.id, {
            item_id: item.inventory_id,
            quantity: item.qty,
            unit: item.unit,
          }).then(({ response, data }) => {
            if (!response.ok) throw new Error("server error");
            return { type: "post", item, newId: data.id };
          })
        ),
      ]);

      const nextDirty = new Set(dirtyOrderItemIds.value);
      results.forEach((r) => {
        if (r.type === "patch") {
          nextDirty.delete(r.item.order_item_id);
          persistedItemValues.set(r.item.order_item_id, { qty: r.item.qty, unit: r.item.unit });
        }
      });
      dirtyOrderItemIds.value = nextDirty;

      const postResults = results.filter((r) => r.type === "post");
      if (postResults.length > 0) {
        orderItems.value = orderItems.value.map((o) => {
          const result = postResults.find((r) => r.item.key === o.key);
          if (!result) return o;
          persistedItemValues.set(result.newId, { qty: o.qty, unit: o.unit });
          return { ...o, order_item_id: result.newId };
        });
      }

      Flash.success(`Order ${order.visual_id} updated`);
    } catch {
      Flash.error("Failed to update order. Please try again.");
    } finally {
      const next = new Set(updating.value);
      next.delete(supplierKey);
      updating.value = next;
    }
  }

  function buildOrderPayload(supplierKey, items) {
    return {
      supplier_id: supplierKey === "__none__" ? null : parseInt(supplierKey),
      items: items.map((i) => ({ inventory_id: i.inventory_id, quantity: i.qty, unit: i.unit })),
    };
  }

  async function createOrder(supplierKey) {
    const items = orderGroups.value.find(([k]) => k === supplierKey)?.[1] ?? [];
    creating.value = new Set([...creating.value, supplierKey]);
    try {
      const { response, data } = await requestJson(props.createOrdersPath, {
        method: "POST",
        body: JSON.stringify({ proposed_orders: [buildOrderPayload(supplierKey, items)] }),
      });
      if (!response.ok) throw new Error("server error");
      const result = data[0];
      persistedOrders.value = new Map([...persistedOrders.value, [supplierKey, result]]);
      const itemIdMap = new Map(result.items.map((i) => [i.inventory_id, i.order_item_id]));
      orderItems.value = orderItems.value.map((o) => {
        const newId = itemIdMap.get(o.inventory_id);
        if (!newId || o.order_item_id) return o;
        persistedItemValues.set(newId, { qty: o.qty, unit: o.unit });
        return { ...o, order_item_id: newId };
      });
      Flash.success(`Order ${result.visual_id} created for ${supplierLabel(supplierKey)}`);
    } catch {
      Flash.error("Failed to create order. Please try again.");
    } finally {
      const next = new Set(creating.value);
      next.delete(supplierKey);
      creating.value = next;
    }
  }

  async function createAll() {
    const groups = uncreatedGroups.value;
    const keys = groups.map(([k]) => k);
    creating.value = new Set([...creating.value, ...keys]);
    try {
      const { response, data } = await requestJson(props.createOrdersPath, {
        method: "POST",
        body: JSON.stringify({
          proposed_orders: groups.map(([key, items]) => buildOrderPayload(key, items)),
        }),
      });
      if (!response.ok) throw new Error("server error");
      const next = new Map(persistedOrders.value);
      keys.forEach((key, i) => next.set(key, data[i]));
      persistedOrders.value = next;
      Flash.success(`${keys.length} order${keys.length !== 1 ? "s" : ""} created`);
    } catch {
      Flash.error("Failed to create orders. Please try again.");
    } finally {
      const next = new Set(creating.value);
      keys.forEach((k) => next.delete(k));
      creating.value = next;
    }
  }
</script>

<style lang="scss" scoped>
  @import "~jose_modern/css";

  .po-ingredients-box {
    overflow: hidden;
  }

  .po-section-header {
    border-bottom: 1px solid $border;
  }

  .po-bulk-bar {
    background: #ebf8f0;
    border-bottom: 1px solid #ade5c0;
  }

  .po-ingredients-table {
    min-width: 900px;

    th,
    :deep(td) {
      padding-left: 1rem;
      padding-right: 1rem;
      vertical-align: top;
    }

    th input[type="checkbox"] {
      cursor: pointer;
    }
  }

  .po-moved-footer {
    border-top: 1px solid $border;
    background: $light;
  }

  .po-divider-line {
    flex: 1;
    height: 1px;
    background: $border;
  }

  .po-orders-empty {
    border: 2px dashed $border;
    border-radius: $radius-large;
  }
</style>
